/**
 * Sidebar Menu Configuration
 * All menu items and groups are defined here as constants
 */

import {
  LayoutDashboard,
  BarChart3,
  FileText,
  ShoppingCart,
  Truck,
  Wallet,
  CreditCard,
  Blocks,
  Users,
  CarFront,
  Settings,
  Shield,
  Building2,
  MapPin,
  Package,
  Grid,
  Factory,
  MessageCircle,
} from '@spcc/uikit'
import type { MenuItem, MenuGroup } from './types'

// ============================================
// Route Patterns
// ============================================

export const ROUTE_PATTERNS = {
  SALES: ['/sales-orders', '/orders', '/customers'],
  FINANCE: '/finance',
  FLEET: '/fleet',
  ADMIN: '/admin',
} as const

// ============================================
// Menu Item Helpers
// ============================================

export function isSalesRoute(pathname: string): boolean {
  return ROUTE_PATTERNS.SALES.some((p) => pathname.startsWith(p))
}

export function isFinanceRoute(pathname: string): boolean {
  return pathname.startsWith(ROUTE_PATTERNS.FINANCE)
}

export function isFleetRoute(pathname: string): boolean {
  return pathname.startsWith(ROUTE_PATTERNS.FLEET)
}

export function isAdminRoute(pathname: string): boolean {
  return pathname.startsWith(ROUTE_PATTERNS.ADMIN)
}

// ============================================
// Main Menu Items
// ============================================

export const MAIN_MENU_ITEMS: MenuItem[] = [
  {
    to: '/',
    labelKey: 'sidebar.overview',
    icon: LayoutDashboard,
  },
]

// ============================================
// Sales & Fulfillment Menu Group
// ============================================

export const SALES_MENU_ITEMS: MenuItem[] = [
  {
    to: '/sales-orders',
    labelKey: 'sidebar.salesOrders',
    icon: FileText,
  },
  {
    to: '/orders',
    labelKey: 'sidebar.orders',
    icon: ShoppingCart,
  },
  {
    to: '/customers',
    labelKey: 'sidebar.customers',
    icon: Users,
  },
  {
    to: '/customers-dashboard',
    labelKey: 'sidebar.customersDashboard',
    icon: BarChart3,
  },
]

export const SALES_MENU_GROUP: MenuGroup = {
  id: 'sales',
  labelKey: 'sidebar.salesFulfillment',
  icon: ShoppingCart,
  items: SALES_MENU_ITEMS,
  isActivePath: isSalesRoute,
}

// ============================================
// Finance Menu Group
// ============================================

export const FINANCE_MENU_ITEMS: MenuItem[] = [
  {
    to: '/finance/payments',
    labelKey: 'sidebar.payments',
    icon: CreditCard,
  },
  {
    to: '/finance/invoices',
    labelKey: 'sidebar.invoices',
    icon: FileText,
  },
]

export const FINANCE_MENU_GROUP: MenuGroup = {
  id: 'finance',
  labelKey: 'sidebar.finance',
  icon: Wallet,
  items: FINANCE_MENU_ITEMS,
  isActivePath: isFinanceRoute,
}

// ============================================
// Fleet Menu Group
// ============================================

export const FLEET_MENU_ITEMS: MenuItem[] = [
  {
    to: '/fleet/resources',
    labelKey: 'sidebar.resources',
    icon: Blocks,
  },
  {
    to: '/fleet/drivers',
    labelKey: 'sidebar.drivers',
    icon: Users,
  },
  {
    to: '/fleet/vehicles',
    labelKey: 'sidebar.vehicles',
    icon: CarFront,
  },
]

export const FLEET_MENU_GROUP: MenuGroup = {
  id: 'fleet',
  labelKey: 'sidebar.fleet',
  icon: Truck,
  items: FLEET_MENU_ITEMS,
  isActivePath: isFleetRoute,
}

// ============================================
// Admin Menu Group
// ============================================

export const ADMIN_MENU_ITEMS: MenuItem[] = [
  {
    to: '/admin',
    labelKey: 'sidebar.adminDashboard',
    icon: LayoutDashboard,
  },
  {
    to: '/admin/tenants',
    labelKey: 'sidebar.tenants',
    icon: Building2,
  },
  {
    to: '/admin/users',
    labelKey: 'sidebar.users',
    icon: Users,
  },
  {
    to: '/admin/customer-accounts',
    labelKey: 'sidebar.customerAccounts',
    icon: CreditCard,
  },
  {
    to: '/admin/factories',
    labelKey: 'sidebar.factories',
    icon: Factory,
  },
  {
    to: '/admin/regions',
    labelKey: 'sidebar.regions',
    icon: MapPin,
  },
  {
    to: '/admin/cities',
    labelKey: 'sidebar.cities',
    icon: Building2,
  },
  {
    to: '/admin/products',
    labelKey: 'sidebar.products',
    icon: Package,
  },
  {
    to: '/admin/availability',
    labelKey: 'sidebar.availability',
    icon: Grid,
  },
]

export const ADMIN_MENU_GROUP: MenuGroup = {
  id: 'admin',
  labelKey: 'sidebar.systemAdmin',
  icon: Shield,
  items: ADMIN_MENU_ITEMS,
  isActivePath: isAdminRoute,
  isAdminOnly: true,
}

// ============================================
// All Menu Groups (for iteration)
// ============================================

export const MENU_GROUPS: MenuGroup[] = [
  SALES_MENU_GROUP,
  FINANCE_MENU_GROUP,
  FLEET_MENU_GROUP,
  ADMIN_MENU_GROUP,
]

// ============================================
// Secondary Menu Items
// ============================================

export const SECONDARY_MENU_ITEMS: MenuItem[] = [
  {
    to: '/support',
    labelKey: 'sidebar.support',
    icon: MessageCircle,
  },
]

// ============================================
// Bottom Menu Items
// ============================================

export const BOTTOM_MENU_ITEMS: MenuItem[] = [
  {
    to: '/settings',
    labelKey: 'sidebar.settings',
    icon: Settings,
  },
]

// ============================================
// Default Expanded Menu IDs
// ============================================

export const DEFAULT_EXPANDED_MENUS = ['sales', 'finance', 'fleet', 'admin']

// ============================================
// Sidebar Dimensions
// ============================================

export const SIDEBAR_DIMENSIONS = {
  EXPANDED_WIDTH: 'w-72',
  COLLAPSED_WIDTH: 'w-[72px]',
  MOBILE_WIDTH: 'w-72',
} as const
